/**
 * DsaOfferCard — rich, comparable decision card for DSA payout offers.
 *
 * Design principles (industry-standard, mirrors LinkedIn job cards / Airbnb /
 * Stripe partner cards):
 *  • Bank name is the visual hero (no redundant "BANK" badge — implied).
 *  • Location gets its OWN row (critical info, prominent).
 *  • Detail rows are FIXED-POSITION SLOTS — every card renders the same 7
 *    rows so cards line up vertically and a user can scan the grid like a
 *    comparison table. Missing data renders muted ("Any" / "Flexible") —
 *    the row is never collapsed.
 *  • Lender type badge ONLY shown for NBFC / Fintech (when it adds info).
 *  • Commission gets its own soft-green band — the closing punchline.
 *  • DSA promotional info docked at the FOOTER (where promo belongs).
 *  • Aesthetic, not loud: muted text + subtle dividers + iconography +
 *    a small first-letter avatar for the bank.
 *
 * Used by: BrowseOffers (buyer browse) and MyOffers (DSA dashboard).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import {
    getDsaOfferStatusConfig,
    getDsaLoanTypeConfig,
    formatCommission,
    formatLoanRange,
    getDeadlineUrgency,
    formatTimeAgo,
} from '../../../services/dsaOfferService';

// Status → severity (owner My-Offers).
const STATUS_SEVERITY = { active: 'success', paused: 'warning', expired: 'danger' };

// Non-bank lender types deserve a badge; "bank" is obvious from the name.
const LENDER_TYPE_SEVERITY = { nbfc: 'help', fintech: 'warning' };

const EMPLOYMENT_LABEL = { salaried: 'Salaried', self_employed: 'Self-Employed', both: 'Salaried & Self-Emp' };

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// One reserved-slot detail row. Always rendered — missing data shows muted.
const DetailRow = ({ icon, label, value, present, last }) => (
    <div className={`flex align-items-center gap-2 py-1 ${last ? '' : 'border-bottom-1 surface-border'}`}>
        <i className={`pi ${icon} text-primary flex-shrink-0`} style={{ fontSize: '0.7rem', width: '0.85rem' }} aria-hidden="true" />
        <span className="text-500 text-xs flex-shrink-0" style={{ minWidth: '5.5rem' }}>{label}</span>
        <span
            className={`text-xs font-medium ml-auto text-right white-space-nowrap overflow-hidden text-overflow-ellipsis ${present ? 'text-900' : 'text-400'}`}
            title={String(value)}
        >
            {value}
        </span>
    </div>
);

const DsaOfferCard = ({
    offer,
    onView,
    onInvite,
    showActions = true,
    isOwner = false,
    viewMode = 'grid',
    onPause,
    onResume,
    onEdit,
    onDelete,
    className = '',
    renderActions = null,
}) => {
    const navigate = useNavigate();

    const {
        id,
        bank_name,
        loan_type,
        segment,
        commission_percentage,
        min_loan_amount,
        max_loan_amount,
        min_cibil,
        processing_time,
        city,
        state,
        disbursement_deadline,
        status = 'active',
        view_count = 0,
        invite_count = 0,
        created_at,
        posted_by,
        lender_type,
        loan_type_category,
        approval_rate,
        payout_type,
        payout_timeline,
        est_payout_min,
        est_payout_max,
        eligible_employment,
        min_age,
        max_age,
        min_monthly_income,
        tenure_months_min,
        tenure_months_max,
        digital_journey,
        documents_required = [],
    } = offer;

    const loanTypeConfig = getDsaLoanTypeConfig(loan_type);
    const statusConfig = getDsaOfferStatusConfig(status);
    const deadlineInfo = getDeadlineUrgency(disbursement_deadline);

    // DSA promotional fields (from AgentProfile via the posted_by relation).
    const dsa = posted_by || offer.dsa || {};
    const dsaName = dsa.display_name || dsa.full_name || dsa.name || 'Unknown DSA';
    const dsaPhoto = dsa.profile_photo_url_full || dsa.avatar || null;
    const dsaRating = dsa.rating != null && !Number.isNaN(Number(dsa.rating)) ? Number(dsa.rating) : null;
    const dsaDeals = dsa.deals_closed ?? dsa.completed_deals_count ?? 0;
    const dsaResponse = dsa.response_time || null;
    const isVerified = dsa.is_verified || false;

    const employmentLabel = eligible_employment ? EMPLOYMENT_LABEL[eligible_employment] || eligible_employment : null;
    const approvalPct = approval_rate != null
        ? (typeof approval_rate === 'string' ? parseFloat(approval_rate) : Number(approval_rate))
        : null;
    const hasEstPayout = est_payout_min != null && est_payout_max != null && est_payout_max > 0;
    const locationLabel = [city, state].filter(Boolean).join(', ');

    const handleViewDetails = (e) => {
        if (e) e.stopPropagation();
        if (onView) onView(offer);
        else navigate(`/app/dsa-offers/${id}`);
    };
    const handleInvite = (e) => {
        e.stopPropagation();
        if (onInvite) onInvite(offer);
    };

    // ════════════════════════════════════════════════════════════════════════
    // LIST VIEW — dense horizontal row (table-style scan)
    // ════════════════════════════════════════════════════════════════════════
    if (viewMode === 'list') {
        return (
            <div
                className={`surface-card border-bottom-1 surface-border px-3 py-2 cursor-pointer hover:surface-100 transition-colors transition-duration-150 ${className}`}
                onClick={handleViewDetails}
            >
                <div className="flex align-items-center gap-3">
                    {/* Bank initial avatar */}
                    <div
                        className="flex align-items-center justify-content-center border-round bg-primary-50 text-primary font-bold flex-shrink-0"
                        style={{ width: '2.25rem', height: '2.25rem', fontSize: '0.9rem' }}
                        aria-hidden="true"
                    >
                        {bank_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-shrink-0 min-w-0" style={{ width: '180px' }}>
                        <div className="font-semibold text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">{bank_name}</div>
                        <div className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {loan_type_category || loanTypeConfig.label}
                            {lender_type && lender_type !== 'bank' && ` · ${lender_type.toUpperCase()}`}
                        </div>
                    </div>
                    <div className="flex-shrink-0 hidden md:flex flex-column" style={{ width: '160px' }}>
                        <span className="text-xs text-500">Location</span>
                        <span className="text-sm text-900 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {locationLabel || '—'}
                        </span>
                    </div>
                    <div className="flex-1 hidden lg:flex flex-column">
                        <span className="text-xs text-500">Loan range</span>
                        <span className="text-sm text-900 font-medium">{formatLoanRange(min_loan_amount, max_loan_amount)}</span>
                    </div>
                    <div className="flex-shrink-0 text-right" style={{ minWidth: '110px' }}>
                        <div className="text-green-600 font-bold text-sm">{formatCommission(commission_percentage)}</div>
                        {hasEstPayout && (
                            <div className="text-500 text-xs white-space-nowrap">{inr(est_payout_min)}–{inr(est_payout_max)}</div>
                        )}
                    </div>
                    {showActions && !isOwner && (
                        <Button icon="pi pi-send" label="Enquire" severity="success" size="small" onClick={handleInvite} />
                    )}
                    {showActions && isOwner && renderActions && (
                        <div onClick={(e) => e.stopPropagation()}>{renderActions(offer)}</div>
                    )}
                </div>
            </div>
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // GRID VIEW — fixed-row decision card
    // ════════════════════════════════════════════════════════════════════════
    return (
        <div
            className={`surface-card border-round border-1 surface-border cursor-pointer hover:shadow-3 transition-all transition-duration-200 h-full flex flex-column overflow-hidden ${className}`}
            onClick={handleViewDetails}
        >
            {/* ── Header: bank (with initial avatar) + product · rating + age ── */}
            <div className="p-3 pb-2">
                <div className="flex align-items-start justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 flex-1 min-w-0">
                        <div
                            className="flex align-items-center justify-content-center border-round bg-primary-50 text-primary font-bold flex-shrink-0"
                            style={{ width: '2.25rem', height: '2.25rem', fontSize: '0.95rem' }}
                            aria-hidden="true"
                        >
                            {bank_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-900 text-base white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {bank_name}
                            </div>
                            <div className="text-xs text-600 flex align-items-center gap-1 flex-wrap">
                                <i className={`pi ${loanTypeConfig.icon || 'pi-tag'} text-primary`} style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                                <span className="font-medium">{loan_type_category || loanTypeConfig.label}</span>
                                {segment && (
                                    <>
                                        <span className="text-400">·</span>
                                        <span>{segment}</span>
                                    </>
                                )}
                                {/* Only badge non-bank lenders — "BANK" is obvious from the name */}
                                {lender_type && lender_type !== 'bank' && (
                                    <Tag
                                        value={lender_type.toUpperCase()}
                                        severity={LENDER_TYPE_SEVERITY[lender_type] || 'secondary'}
                                        className="text-xs"
                                        style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}
                                    />
                                )}
                                {isOwner && (
                                    <Tag value={statusConfig.label} severity={STATUS_SEVERITY[status] || statusConfig.severity} className="text-xs capitalize ml-auto" />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-column align-items-end gap-1 flex-shrink-0">
                        {showActions && isOwner && renderActions && (
                            <div onClick={(e) => e.stopPropagation()}>{renderActions(offer)}</div>
                        )}
                        {dsaRating != null && dsaRating > 0 && (
                            <div className="flex align-items-center gap-1" aria-label={`Rating ${dsaRating}`}>
                                <i className="pi pi-star-fill text-yellow-500" style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                                <span className="font-medium text-900 text-xs">{dsaRating.toFixed(1)}</span>
                            </div>
                        )}
                        <span className="text-400 text-xs white-space-nowrap">{formatTimeAgo(created_at)}</span>
                    </div>
                </div>

                {/* ── Location row — prominent, dedicated line ── */}
                <div className="flex align-items-center gap-1 text-xs mt-2 min-w-0">
                    <i className="pi pi-map-marker text-primary flex-shrink-0" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                    <span className={`font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis ${locationLabel ? 'text-700' : 'text-400'}`}>
                        {locationLabel || 'Pan-India'}
                    </span>
                </div>
            </div>

            {/* ── Fixed-row details — every card renders these 7 slots ── */}
            <div className="px-3 pb-2">
                <div className="surface-50 border-round p-2">
                    <DetailRow
                        icon="pi-wallet"
                        label="Loan range"
                        value={(min_loan_amount && max_loan_amount) ? formatLoanRange(min_loan_amount, max_loan_amount) : 'Not specified'}
                        present={!!(min_loan_amount && max_loan_amount)}
                    />
                    <DetailRow
                        icon="pi-id-card"
                        label="Min CIBIL"
                        value={min_cibil ? `${min_cibil}+` : 'Any score'}
                        present={!!min_cibil}
                    />
                    <DetailRow
                        icon="pi-user"
                        label="Eligibility"
                        value={employmentLabel || 'Any employment'}
                        present={!!employmentLabel}
                    />
                    <DetailRow
                        icon="pi-calendar"
                        label="Age range"
                        value={(min_age || max_age) ? `${min_age || '—'}–${max_age || '—'} yrs` : 'Any age'}
                        present={!!(min_age || max_age)}
                    />
                    <DetailRow
                        icon="pi-indian-rupee"
                        label="Min income"
                        value={min_monthly_income ? `${inr(min_monthly_income)} / mo` : 'No minimum'}
                        present={!!min_monthly_income}
                    />
                    <DetailRow
                        icon="pi-clock"
                        label="Tenure"
                        value={(tenure_months_min || tenure_months_max) ? `${tenure_months_min || '—'}–${tenure_months_max || '—'} months` : 'Flexible'}
                        present={!!(tenure_months_min || tenure_months_max)}
                    />
                    <DetailRow
                        icon="pi-bolt"
                        label="Processing"
                        value={processing_time || 'Not specified'}
                        present={!!processing_time}
                        last
                    />
                </div>
            </div>

            {/* ── Meta strip (reserved min-height so cards align) ── */}
            <div className="px-3 pb-2 flex align-items-center flex-wrap gap-2 text-xs" style={{ minHeight: '1.75rem' }}>
                {Array.isArray(documents_required) && documents_required.length > 0 && (
                    <span className="inline-flex align-items-center gap-1 text-700">
                        <i className="pi pi-file text-500" aria-hidden="true" />{documents_required.length} docs
                    </span>
                )}
                {digital_journey && (
                    <span className="inline-flex align-items-center gap-1 text-green-600 font-medium">
                        <i className="pi pi-bolt" aria-hidden="true" />Digital
                    </span>
                )}
                {approvalPct != null && (
                    <span className="inline-flex align-items-center gap-1 text-700">
                        <i className="pi pi-check-circle text-500" aria-hidden="true" />{Math.round(approvalPct)}% approval
                    </span>
                )}
                {deadlineInfo.daysLeft != null && deadlineInfo.level !== 'relaxed' && (
                    <span className={`ml-auto inline-flex align-items-center gap-1 font-medium ${
                        deadlineInfo.severity === 'danger' ? 'text-red-500'
                        : deadlineInfo.severity === 'warning' ? 'text-orange-500'
                        : 'text-blue-600'
                    }`}>
                        <i className="pi pi-clock" aria-hidden="true" />{deadlineInfo.label}
                    </span>
                )}
            </div>

            {/* ── Commission punchline — soft green band, always rendered ── */}
            <div className="mx-3 mb-2 border-round p-2" style={{ backgroundColor: 'var(--green-50)' }}>
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                    <div className="flex align-items-center gap-2">
                        <span className="text-green-700 font-bold text-base">
                            {formatCommission(commission_percentage)}
                        </span>
                        {payout_type && (
                            <Tag
                                value={payout_type.charAt(0).toUpperCase() + payout_type.slice(1)}
                                severity={payout_type === 'instant' ? 'success' : payout_type === 'advance' ? 'info' : 'secondary'}
                                className="text-xs capitalize"
                                style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}
                            />
                        )}
                    </div>
                    <span className={`text-xs font-medium ${hasEstPayout ? 'text-green-700' : 'text-400'}`}>
                        {hasEstPayout ? `Earn ${inr(est_payout_min)}–${inr(est_payout_max)}` : 'Earn varies by amount'}
                    </span>
                </div>
                {payout_timeline && (
                    <div className="text-600 text-xs mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {payout_timeline}
                    </div>
                )}
            </div>

            {/* ── DSA promo footer — surface-50 band at the bottom ── */}
            <div className="mt-auto p-3 pt-2 surface-50 border-top-1 surface-border">
                <div className="flex align-items-center gap-2 mb-2 min-w-0">
                    <Avatar
                        image={dsaPhoto}
                        icon={!dsaPhoto ? 'pi pi-user' : undefined}
                        shape="circle"
                        className="bg-primary-100 flex-shrink-0"
                        style={{ width: '1.5rem', height: '1.5rem' }}
                    />
                    <div className="flex-1 min-w-0 flex align-items-center gap-1 flex-wrap text-xs">
                        <span className="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ maxWidth: '9rem' }}>
                            {dsaName}
                        </span>
                        {isVerified && <i className="pi pi-verified text-primary flex-shrink-0" style={{ fontSize: '0.7rem' }} aria-label="Verified" />}
                        {dsaDeals > 0 && <span className="text-500 white-space-nowrap">· {dsaDeals} deals</span>}
                        {dsaResponse && <span className="text-500 white-space-nowrap">· {dsaResponse}</span>}
                        {!isOwner && (invite_count > 0 || view_count > 0) && (
                            <span className="ml-auto text-400 white-space-nowrap">
                                {invite_count > 0 && <><i className="pi pi-send" /> {invite_count} </>}
                                {view_count > 0 && <><i className="pi pi-eye" /> {view_count}</>}
                            </span>
                        )}
                    </div>
                </div>

                {/* Buyer actions */}
                {showActions && !renderActions && !isOwner && (
                    <div className="flex gap-2">
                        <Button icon="pi pi-send" label="Enquire" severity="success" size="small" className="flex-1" onClick={handleInvite} />
                        <Button icon="pi pi-arrow-right" iconPos="right" label="View" outlined severity="secondary" size="small" className="flex-1" onClick={handleViewDetails} />
                    </div>
                )}
                {/* Owner actions */}
                {showActions && !renderActions && isOwner && (
                    <div className="flex gap-2">
                        <Button label="Edit" icon="pi pi-pencil" outlined size="small" className="flex-1"
                            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(offer); }} />
                        {status === 'active' && (
                            <Button icon="pi pi-pause" severity="warning" size="small" aria-label="Pause"
                                onClick={(e) => { e.stopPropagation(); onPause && onPause(offer); }} />
                        )}
                        {status === 'paused' && (
                            <Button icon="pi pi-play" severity="success" size="small" aria-label="Resume"
                                onClick={(e) => { e.stopPropagation(); onResume && onResume(offer); }} />
                        )}
                        <Button icon="pi pi-trash" severity="danger" outlined size="small" aria-label="Delete"
                            onClick={(e) => { e.stopPropagation(); onDelete && onDelete(offer); }} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DsaOfferCard;
